import React, { useEffect } from 'react';
import Switch from './Switch.jsx';
import Button from './Button.jsx';
import { PREFERENCES_LOADING, PREFERENCES_UNAVAILABLE, useFeedback } from '../state/FeedbackPreferences.jsx';

const choices = [
  ['completionChime', 'Completion chime'], ['paydayFanfare', 'Payday fanfare'],
  ['villageAmbience', 'Soft village ambience'], ['kioskSound', 'Allow sound on the kiosk'],
  ['softHaptic', 'One soft haptic after a successful kindle'],
];
export default function FeedbackControls() {
  const feedback = useFeedback();
  return <section className="set-card" aria-label="Sound and haptics">
    <h2>Sound and haptics</h2>
    <p>Off until a parent chooses. Silence never changes a reward.</p>
    {feedback.availability === 'unavailable' ? <p role="alert">{PREFERENCES_UNAVAILABLE}</p> : null}
    {feedback.availability === 'loading' ? <p>{PREFERENCES_LOADING}</p> : null}
    {!feedback.canEdit && feedback.availability === 'ready' ? <p>A connected parent profile can change these preferences.</p> : null}
    {!feedback.assetsAvailable ? <p>Audio tracks are not connected yet. These controls prepare your preferences.</p> : null}
    {choices.map(([key, label]) => <div className="set-row" key={key}>
      <Switch label={label} checked={feedback.preferences[key]} disabled={!feedback.canEdit || feedback.busy}
        onCheckedChange={value => feedback.setPreference(key, value)} />
    </div>)}
    <p role="status">{feedback.message}</p>
  </section>;
}

/**
 * #577 — the shared display's completion chime.
 *
 * The display's completion handler lives above its feedback provider, so it hands its saved
 * completions down as `receipts` (`{ [questId]: { executionId, status } }`) and this chimes once for
 * each NEW one. Receipts already present when this mounts are marked as heard, never played: a
 * display waking up or re-reading its board must not replay earlier lanterns. `kindle` dedupes by
 * execution id as well, and the provider's own gates decide whether the kiosk may sound at all.
 */
export function KioskChime({ receipts }) {
  const feedback = useFeedback();
  const kindle = React.useRef(feedback.kindle);
  kindle.current = feedback.kindle;
  const heard = React.useRef(null);
  useEffect(() => {
    const list = Object.values(receipts || {}).filter(r => r && r.executionId != null);
    if (heard.current === null) {
      heard.current = new Set(list.map(r => String(r.executionId)));
      return;
    }
    for (const receipt of list) {
      const id = String(receipt.executionId);
      if (heard.current.has(id)) continue;
      heard.current.add(id);
      kindle.current({ executionId: receipt.executionId, status: receipt.status }, 'kiosk');
    }
  }, [receipts]);
  return null;
}

export function VillageAmbience({ surface = 'app' }) {
  const feedback = useFeedback();
  // Unmount stops any explicitly started loop. A page mount never starts one.
  const stop = React.useRef(feedback.stopAmbience);
  stop.current = feedback.stopAmbience;
  useEffect(() => () => stop.current(), []);
  const allowed = feedback.preferences.villageAmbience && feedback.ambienceAvailable &&
    (surface !== 'kiosk' || feedback.preferences.kioskSound);
  return <div aria-label="Village sound" style={{ display: 'flex', gap: 8, padding: 12, flexWrap: 'wrap' }}>
    <Button variant="quiet" disabled={!allowed} onClick={() => feedback.startAmbience(surface)}>Play village ambience</Button>
    <Button variant="quiet" disabled={!allowed} onClick={feedback.stopAmbience}>Keep the village quiet</Button>
  </div>;
}
